import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import PaymentMethodSetup, {
  PaymentMethodSetupHandle,
  PaymentMethodSetupState,
  SavedPaymentMethod,
} from "components/payment/PaymentMethodSetup/PaymentMethodSetup";
import { removeSetupReturnParams } from "components/profile/paymentMethodSetupReturn";
import MobileAndEmailAuthentication from "components/auth/ExistingMobileEmailAuthentication/MobileAndEmailAuthentication";
import { useAnalytics, useSnackbar } from "globals/hooks";
import { LockIcon } from "design-system/icons";
import { fromGlobalId } from "utils/auth/helpers";
import {
  clearPaymentLinkSetup,
  PaymentLinkSetupContext,
  PaymentLinkSetupReturn,
  retainPaymentLinkSetup,
} from "../paymentLinkSetupReturn";
import PayButton from "./PayButton";
import LinkPaymentMethod from "components/globals/LinkPaymentMethod";
import { isLinkPaymentMethod } from "globals/utils/paymentMethods";

type Props = {
  operatorId: string;
  currency: string;
  context: PaymentLinkSetupContext;
  resume?: PaymentLinkSetupReturn;
  totalDue: string;
  companyId?: string;
  existingCompanyPaymentMethodIds?: string[];
  authenticatedContactId?: string;
  savedPaymentMethods?: SavedPaymentMethod[];
  savedCardsOnly?: boolean;
  billingDetails?: { name?: string; email?: string; phone?: string };
  isLoading: boolean;
  onCreatePayment: (paymentMethodId: string, clientSecret?: string) => void;
};

function PaymentElementChargeCustomerBlock(props: Props) {
  const {
    operatorId,
    context,
    totalDue,
    companyId,
    existingCompanyPaymentMethodIds,
    authenticatedContactId,
    billingDetails,
    isLoading,
    onCreatePayment,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const snackbar = useSnackbar();
  const { track } = useAnalytics();
  const setupRef = useRef<PaymentMethodSetupHandle>(null);
  const submittingRef = useRef(false);
  const setupIntentIdRef = useRef<string | null>(null);
  const [resume, setResume] = useState(props.resume);
  const [setupState, setSetupState] = useState<PaymentMethodSetupState | null>(
    null
  );
  const savedMethod = setupState?.result;
  const [restartCount, setRestartCount] = useState(0);

  const clearReturn = () => {
    if (setupIntentIdRef.current || resume) {
      clearPaymentLinkSetup(setupIntentIdRef.current || resume.setupIntentId);
    }
    if (resume) {
      history.replace({
        ...location,
        search: removeSetupReturnParams(location.search),
      });
    }
  };

  const restart = () => {
    if (isLoading || submittingRef.current) return;
    clearReturn();
    setResume(undefined);
    setSetupState(null);
    setRestartCount((count) => count + 1);
  };

  const saveCard = async () => {
    const setup = setupRef.current;
    if (!setup || submittingRef.current || savedMethod) return;
    submittingRef.current = true;
    try {
      const method = await setup.save();
      if (setupRef.current !== setup) return;
      clearReturn();
      if (companyId && !existingCompanyPaymentMethodIds?.includes(method.id)) {
        track("company_creditCardAdded", {
          company_id: fromGlobalId(companyId).id,
        });
      }
      return method;
    } catch (error) {
      if (setupRef.current !== setup) return;
      snackbar.error(
        error instanceof Error
          ? error.message
          : "Unable to save card. Please try again."
      );
    } finally {
      submittingRef.current = false;
    }
  };
  const saveCardRef = useRef(saveCard);
  saveCardRef.current = saveCard;

  useEffect(() => {
    if (resume && setupState?.ready) saveCardRef.current();
  }, [resume, setupState?.ready]);

  const handlePay = async () => {
    if (isLoading || submittingRef.current) return;
    if (savedMethod) {
      onCreatePayment(savedMethod.id, savedMethod.clientSecret);
      return;
    }
    const setup = setupRef.current;
    const method = await saveCard();
    if (method && setupRef.current === setup)
      onCreatePayment(method.id, method.clientSecret);
  };

  return (
    <>
      <Box mb={2} display="flex" flexDirection="column">
        {!authenticatedContactId && <MobileAndEmailAuthentication />}
        <Box mb={2}>
          <Typography variant="h4">Payment method</Typography>
        </Box>
        {savedMethod ? (
          <Box>
            <Typography>
              {isLinkPaymentMethod(savedMethod) ? (
                <LinkPaymentMethod />
              ) : (
                `Card ending in ${savedMethod.card.last4}`
              )}{" "}
              is ready. Select Pay to submit payment.
            </Typography>
            <Button disabled={isLoading} onClick={restart}>
              Use a different card
            </Button>
          </Box>
        ) : resume ? (
          <Typography>
            Checking card verification. Payment will be submitted when you
            select Pay.
          </Typography>
        ) : null}
        <Box my={1} display={savedMethod ? "none" : undefined}>
          <PaymentMethodSetup
            key={restartCount}
            ref={setupRef}
            operatorId={operatorId}
            currency={props.currency}
            savedPaymentMethods={props.savedPaymentMethods}
            savedCardsOnly={props.savedCardsOnly}
            canSaveForFutureBookings={
              !!authenticatedContactId &&
              context.payerId === authenticatedContactId
            }
            attemptScope={authenticatedContactId}
            attemptGeneration={restartCount}
            context={context}
            billingDetails={billingDetails || {}}
            returnUrl={`${location.pathname}${removeSetupReturnParams(location.search)}`}
            resume={resume}
            onBeforeConfirm={(setupIntentId) => {
              retainPaymentLinkSetup(
                operatorId,
                context,
                location.pathname,
                setupIntentId,
                authenticatedContactId
              );
              setupIntentIdRef.current = setupIntentId;
            }}
            onStateChange={setSetupState}
          />
          {setupState?.restartRequired && (
            <Button onClick={restart}>Enter card again</Button>
          )}
        </Box>
      </Box>
      <PayButton
        isDisabled={isLoading || setupState?.busy}
        isLoading={isLoading || setupState?.busy}
        onClick={handlePay}
        totalDue={totalDue}
      />
      <Box mt={2} display="flex" alignItems="center" justifyContent="center">
        <LockIcon size="small" />
        <Box ml={1.5}>
          <Typography variant="overline">Secure Checkout</Typography>
        </Box>
      </Box>
    </>
  );
}

export default PaymentElementChargeCustomerBlock;
